import React, { useEffect, useMemo, useRef, useState } from 'react';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Ranks from '../atoms/Ranks';
import Loader from '../atoms/Loader';
import { COLOR_BORDER, COLOR_SHADOW, COLOR_WHITE } from '../styles/colors';
import gql from '../services/gql';
import Player from '../types/Player';
import Team from '../types/Team';
import Position from '../types/Position';
import {PlayerMultiSportStatsComparison} from '../types/PlayerMultiSportStats';
import College from '../types/College';
import HighSchool from '../types/HighSchool';
import Column from '../types/Column';
import {
  DEFAULT_PROFILE_STATS_COLUMNS,
  getCollegeHeaderItemByColumn,
  getNflHeaderItemByColumn,
  getProfileStatsComparisonRankValue
} from '../services/profile-stats-categories';
import { State } from '../redux/reducers';

interface PlayerMultiSportDataProps {
  player?: Player;
  collegeTeam?: Team;
  nflTeam?: Team;
  position?: Position;
  columns?: Column[];
  isNFL?: boolean;
  categories?: string[];
}

const useStyles = makeStyles((theme:Theme) => ({
  playerMultiSportData: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    background: COLOR_WHITE,
    border: `1px solid ${COLOR_BORDER}`,
    borderTop: 0,
    boxShadow: `0 10px 10px 0 ${COLOR_SHADOW}`,
  },
}), { name: UpdatedPlayerMultiSportData.name });

export default function UpdatedPlayerMultiSportData (props:PlayerMultiSportDataProps) {
  const {
    player,
    collegeTeam,
    nflTeam,
    position,
    columns = [],
    isNFL,
    categories,
  } = props;
  const classes = useStyles();
  const newProfileEnabled = useSelector((state: State) => Boolean(state.user?.program?.newProfileEnabled));
  const isMountedRef = useRef(true);
  const normalizedCategories = useMemo(() => categories || [], [categories]);
  const categoriesKey = useMemo(() => normalizedCategories.join('|'), [normalizedCategories]);

  const [loading, setLoading] = useState<boolean>(true);
  const [multiSportStats, setMultiSportStats] = useState<Array<PlayerMultiSportStatsComparison>>([]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (player?.id && collegeTeam?.id && position) {
      if (!isNFL) {
        // fetchMultiSportStats(player.id, collegeTeam.id, position);
        fetchMultiSportStatsComparision(player.id, collegeTeam.id, position);
      }
      if (isNFL && nflTeam?.id) {
        // fetchNFLMultiSportStats(player.id, collegeTeam.id, nflTeam.id, position);
        fetchNFLMultiSportStatsComparison(player.id, collegeTeam.id, nflTeam.id, position);
      }
    }
  }, [player?.id, collegeTeam?.id, nflTeam?.id, position, isNFL, categoriesKey]);

  function fetchMultiSportStatsComparision (playerId:number, collegeTeamId:number, position:string) {
    if (!loading) setLoading(true);

    gql(`
      getMultisportStats(
        playerId: ${playerId},
        compareToTeamId: ${collegeTeamId},
        positionCode: "${position}",
        categories: [${normalizedCategories.map((category:string) => `"${category}"`).join(', ')}]
      ) {
        multiSportStats {
          performance
          event
          meet
          year
          comparisonStats {
            percentile
            playerRank
            playerCount
          }
        }
      }
    `)
      .then((data:any) => get(data, 'getMultisportStats.multiSportStats') as PlayerMultiSportStatsComparison[])
      .then((multiSportStats:PlayerMultiSportStatsComparison[]) => {
        if (!isMountedRef.current) return;
        setMultiSportStats(multiSportStats);
      })
      .catch(console.error)
      .finally(() => {
        if (!isMountedRef.current) return;
        setLoading(false);
      });
  }

  function fetchNFLMultiSportStatsComparison (playerId:number, collegeTeamId:number, nflTeamId:number, position:string) {
    if (!loading) setLoading(true);

    gql(`
      getMultisportStatsNFL(
        playerId: ${playerId},
        compareToCollegeTeamId: ${collegeTeamId},
        compareToNFLTeamId: ${nflTeamId},
        positionCode: "${position}",
        categories: [${normalizedCategories.map((category:string) => `"${category}"`).join(', ')}]
      ) {
        multiSportStats {
          performance
          event
          meet
          year
          comparisonStats {
            percentile
            playerRank
            playerCount
          }
        }
      }
    `)
      .then((data:any) => get(data, 'getMultisportStatsNFL.multiSportStats') as PlayerMultiSportStatsComparison[])
      .then((multiSportStats:PlayerMultiSportStatsComparison[]) => {
        if (!isMountedRef.current) return;
        setMultiSportStats(multiSportStats);
      })
      .catch(console.error)
      .finally(() => {
        if (!isMountedRef.current) return;
        setLoading(false);
      });
  }

  const college = ((player && player.playerColleges) || [])
    .find((college:College) => college && college.isPrimary);
  const highSchool = ((player && player.highSchools) || [])
    .find((highSchool:HighSchool) => highSchool && highSchool.isPrimary);
  const recruitingClass = (college && college.recruitingClass) || (highSchool && highSchool.graduatingClass);

  const selectedColumns = (columns.filter((column:Column) => column.selected).length
    ? columns.filter((column:Column) => column.selected)
    : DEFAULT_PROFILE_STATS_COLUMNS) as Column[];

  return (
    <div className={classes.playerMultiSportData}>
      <Loader inProgress={loading} />

      {!isNFL && (
        <Ranks
          headerItems={selectedColumns.map((column:Column) => getCollegeHeaderItemByColumn(column, {
            recruitingClass,
            collegeTeam,
          }))}
          noTopBorder
        />
      )}
      {isNFL && (
        <Ranks
          headerItems={selectedColumns.map((column:Column) => getNflHeaderItemByColumn(column, {
            collegeTeam,
            nflTeam,
          }))}
          noTopBorder
        />
      )}

      {(multiSportStats && !!multiSportStats.length) && (
        multiSportStats.map((stat:PlayerMultiSportStatsComparison, index) => (
          <Ranks
            key={index}
            event={{
              type: stat.event
                ? `${stat.event.slice(0, 1).toUpperCase()}${stat.event.slice(1).toLowerCase().replace('_', '')}`
                : ''
            }}
            isNFL={isNFL}
            values={selectedColumns.map((column:Column, columnIndex:number) => {
              const comparisonStats = get(stat, 'comparisonStats', []);
              return getProfileStatsComparisonRankValue({
                column,
                columnIndex,
                comparisonStats,
                isNFL,
                isHSSports: true,
                newProfileEnabled,
              });
            })}
            bottomSpace={index + 1 < multiSportStats.length}
          />
        ))
      )}
    </div>
  );
}